/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file buildPlaceholder internal helper function
 * @author Roland Scott
 * @module react-async-hook\internal\use-async\build-placeholder
 */

import React, { ComponentType, FunctionComponent } from "react";
import { IAsyncFunction } from "../";
import { IAsyncPlaceholderProps, OmitAsyncProps } from "../../";
import { StateRef } from "../types";

/**
 * Create a Placeholder component that binds the caller-supplied placeholder to the async state as a
 * convenience.
 * @param asyncStateContext React context to provide the async state
 * @param Placeholder Placeholder component supplied by the caller
 * @param displayName Async display name for debugging
 */
export function buildPlaceholder<
	TAsync extends IAsyncFunction,
	TPlaceholderProps extends IAsyncPlaceholderProps
>(
	stateRef: StateRef<TAsync>,
	Placeholder?: ComponentType<TPlaceholderProps>,
	displayName?: string,
): FunctionComponent<OmitAsyncProps<TPlaceholderProps>> {
	const AsyncPlaceholder: FunctionComponent<OmitAsyncProps<TPlaceholderProps>> = props => {
		const state = stateRef.current;
		const { children, ...otherProps } = props;
		return (
			<>
				{Placeholder ? (
					<Placeholder {...(otherProps as any)} asyncState={state}>
						{children}
					</Placeholder>
				) : (
					children
				)}
			</>
		);
	};
	// AsyncPlaceholder = memo(AsyncPlaceholder);
	AsyncPlaceholder.displayName = displayName ? `${displayName}Placeholder` : "AsyncPlaceholder";
	return AsyncPlaceholder;
}
